import { action, computed, makeObservable, observable } from 'mobx';
import { QFMFlagKeys, QuestionFeedbackMode } from './obseverableObjects/QuestionFeedbackMode';
import { UiFeedbackState } from './obseverableObjects/UiFeedbackState';
import { ContentType } from '../../Constants';
import { register } from '../../i18n';
import UtilsService from '../services/UtilsService';

const t = register('GlobalQuestionLabels');

export class QuestionFeedbackManager {
  @observable
  questionFeedbackMode = null;

  @observable
  lessonElementFeedbackTracker = new Map();

  get submitButtonLabel() {
    return t('submitButtonLabel');
  }

  get tryAgainButtonLabel() {
    return t('tryAgainButtonLabel');
  }

  get showAnswersButtonLabel() {
    return t('showAnswersButtonLabel');
  }

  get hintButtonLabel() {
    return t('hintButtonLabel');
  }

  get solutionButtonLabel() {
    return t('solutionButtonLabel');
  }

@observable
  openHintDialog = false;

  @observable
  openSolutionDialog = false;

  buttonAvailability = [];
  buttonUsage = [];

  constructor() {
    makeObservable(this);
  }

  @computed get correctFeedback() {
    return this.questionFeedbackMode.correctFeedback;
  }

  @computed get incorrectFeedback() {
    return this.questionFeedbackMode.incorrectFeedback;
  }

  @computed get hasFeedback() {
    return (this.questionFeedbackMode && this.questionFeedbackMode.testItemFeedback && this.questionFeedbackMode.testItemFeedback !== 'none');
  }

  @computed get isPractice() {
    return this.questionFeedbackMode.isPractice;
  }

  @computed get isTTSOverride() {
    return this.questionFeedbackMode.isTTSOverride;
  }

  getAttemptsRemainingText = (lessonElementId) => {
    if (!this.isPractice) {
      return '';
    }

    const ufs = this.lessonElementFeedbackTracker.get(lessonElementId);
    if (!ufs) {
      return '';
    }

    const attemptsUsed = ufs.currentAttemptCount;

    if(attemptsUsed === -1) {
      return '';
    }
 
    const totalAttempts = this.questionFeedbackMode.practiceAttempts;
    const attemptsLeft = totalAttempts - attemptsUsed;

    if (attemptsLeft <= 0) {
      return '';
    }

    if (attemptsLeft === 1) {
      return UtilsService.lastAttempt();
    } else {
      return UtilsService.attemptsLeft(attemptsLeft);
    }
  }

  @computed get noSubmit() {
    const mssb = this.questionFeedbackMode.customPracticeMatrix.get(QFMFlagKeys.mssb);
    return !this.hasFeedback && !mssb;
  }

  getShowAnswerFeedback = (lessonElementId) => {
    const ui = this.lessonElementFeedbackTracker.get(lessonElementId);
    if (ui) {
      return ui.showAnswerFeedback;
    }
    return false;
  }

  getShowAnswers = (lessonElementId) => {
    const ui = this.lessonElementFeedbackTracker.get(lessonElementId);
    if (ui) {
      return ui.showAnswers;
    }
    return false;
  }

  getShowQuestionFeedback = (lessonElementId) => {
    const ui = this.lessonElementFeedbackTracker.get(lessonElementId);
    if (ui) {
      return ui.showQuestionFeedback;
    }
    return false;
  }

  getUiState = (lessonElementId) => {
    return this.lessonElementFeedbackTracker.get(lessonElementId);
  }

  @action
  addButtonUsage = (buttonId) => {
    this.buttonUsage.push(buttonId);
  }

  @action
  addButtonAvailabilty = (buttonId) => {
    this.buttonAvailability.push(buttonId);
  }

  @action
  clearButtonUsage = () => {
    this.buttonUsage = [];
  }

  @action
  clearButtonAvailability = () => {
    this.buttonAvailability = [];
  }

  @action
  setQuestionFeedbackMode = (lessonOptions, modeId) => {
    this.questionFeedbackMode = new QuestionFeedbackMode();
    this.questionFeedbackMode.setStateFromOptions(lessonOptions, modeId);
  }

  @action
  initLessonElementFeedbackState = (lessonElement) => {
    const ufs = new UiFeedbackState();
    ufs.currentAttemptCount = 0;
    this.lessonElementFeedbackTracker.set(lessonElement, ufs);
  }

  @action
  setCurrentAttemptCount = (lessonElementId, count) => {
    const ufs = this.lessonElementFeedbackTracker.get(lessonElementId);
    if (ufs) {
      ufs.currentAttemptCount = count;
    }
  }

  @action
  setSubmitVisibleState = (lessonElementId, state) => {
    const ufs = this.lessonElementFeedbackTracker.get(lessonElementId);
    if (ufs) {
      ufs.submitButtonVisible = state;
    }
  }

  @action
  setSubmitDisabledState = (lessonElementId, state) => {
    const ufs = this.lessonElementFeedbackTracker.get(lessonElementId);
    if (ufs) {
      ufs.submitButtonDisabled = state;
    }
  }

  @action
  setTryAgainVisibleState = (lessonElementId, state) => {
    const ufs = this.lessonElementFeedbackTracker.get(lessonElementId);
    if (ufs) {
      ufs.tryAgainButtonVisible = state;

      if (state) {
        this.addButtonAvailabilty('tryAgain');
      }
    }
  }

  @action
  setLoadingButtonState = (lessonElementId, state) => {
    const ufs = this.lessonElementFeedbackTracker.get(lessonElementId);
    if (ufs) {
      ufs.loadingButtonVisible = state;
    }
  }

  @action
  setShowAnswersVisibleState = (lessonElementId, state) => {
    const ufs = this.lessonElementFeedbackTracker.get(lessonElementId);
    if (ufs) {
      ufs.showAnswersButtonVisible = state;
    }
  }

  @action
  setShowStudentAnswersButtonVisible = (lessonElementId, state) => {
    const ufs = this.lessonElementFeedbackTracker.get(lessonElementId);
    if (ufs) {
      ufs.showStudentAnswersButtonVisible = state;
    }
  }

  @action
  setShowHintVisibleState = (lessonElementId, state) => {
    const ufs = this.lessonElementFeedbackTracker.get(lessonElementId);
    if (ufs) {
      ufs.hintButtonVisible = state;

      if (state) {
        this.addButtonAvailabilty('showHint');
      }
    }
  }

  @action
  setShowSolutionVisibleState = (lessonElementId, state) => {
    const ufs = this.lessonElementFeedbackTracker.get(lessonElementId);
    if (ufs) {
      ufs.solutionButtonVisible = state;

      if (state) {
        this.addButtonAvailabilty('showSolution');
      }
    }
  }

  @action
  setTryAgainButtonLabel = (label) => {
    this.tryAgainButtonLabel = label;
  }

  @action
  setShowAnswerButtonLabel = (label) => {
    this.showAnswersButtonLabel = label;
  }

  @action
  setHintButtonLabel = (label) => {
    this.hintButtonLabel = label;
  }

  @action
  setSolutionButtonLabel = (label) => {
    this.solutionButtonLabel = label;
  }

  @action
  setShowAnswerFeedback = (lessonElementId, state) => {
    const ufs = this.lessonElementFeedbackTracker.get(lessonElementId);
    if (ufs) {
      ufs.showAnswerFeedback = state;
    }
  }

  @action
  setShowQuestionFeedback = (lessonElementId, state) => {
    const ufs = this.lessonElementFeedbackTracker.get(lessonElementId);
    if (ufs) {
      ufs.showQuestionFeedback = state;
    }
  }

  @action
  setShowFeedbackImages = (lessonElementId, state) => {
    const ufs = this.lessonElementFeedbackTracker.get(lessonElementId);
    if (ufs) {
      ufs.showFeedbackImages = state;
    }
  }

  @action
  setShowAnswers = (lessonElementId, state) => {
    const ufs = this.lessonElementFeedbackTracker.get(lessonElementId);
    if (ufs) {
      ufs.showAnswers = state;
      if (state) {
        this.addButtonAvailabilty('showAnswer');
      }
    }
  }

  @action
  setSolutionDialogOpen = (flag) => {
    this.openSolutionDialog = flag;
  }

  @action
  setHintDialogOpen = (flag) => {
    this.openHintDialog = flag;
  }

  @action
  endPractice = (lessonElementId) => {
    const ufs = this.lessonElementFeedbackTracker.get(lessonElementId);
    if (ufs) {
      ufs.currentAttemptCount = this.questionFeedbackMode.practiceAttempts;
    }
  }

  getSubmitButtonLabel = (model) => {
    if ((model.type === ContentType.LONG_TEXT.type || model.type === ContentType.APPLET_QUESTION.type) && model.unscored) {
      return `${t('saveAnswer')}`;
    }
    return this.submitButtonLabel;
  }
}
export default new QuestionFeedbackManager();
